import React, { useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { User, Lock, Eye, EyeOff } from 'lucide-react'
import { useTheme } from '../context/ThemeContext'
import { useAuth } from '../context/AuthContext'
import Navbar from '../components/Navbar'
import RoomEntryModal from '../components/RoomEntryModal'
import RegisterModal from '../components/RegisterModal'
import PromoVideoPopup from '../components/PromoVideoPopup'

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: row;
  background-color: ${props => props.theme.backgroundColor};
`

const Main = styled.div`
  flex: 1;
  margin-left: 240px;
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  background: ${props => props.theme.isDarkMode
    ? 'linear-gradient(135deg, #1e1b3a 0%, #1a1f3a 100%)'
    : 'linear-gradient(135deg, #f3f0ff 0%, #eef2ff 50%, #e0f2fe 100%)'};
  overflow: hidden;

  &::before, &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);
    opacity: 0.4;
    pointer-events: none;
    z-index: 1;
  }
  &::before {
    width: 500px; height: 500px;
    background: ${props => props.theme.isDarkMode ? '#4c1d95' : '#c4b5fd'};
    top: -150px; right: -100px;
  }
  &::after {
    width: 400px; height: 400px;
    background: ${props => props.theme.isDarkMode ? '#1e3a8a' : '#bae6fd'};
    bottom: -120px; right: 30%;
  }

  @media (max-width: 1024px) {
    padding: 24px;
  }
  @media (max-width: 768px) {
    margin-left: 200px;
    padding: 16px;
  }
  @media (max-width: 650px) {
    margin-left: 0;
  }
`

const Container = styled.div`
  position: relative;
  width: 100%;
  max-width: 1100px;
  aspect-ratio: 1.77 / 1;
  max-height: calc(100vh - 80px);
  border-radius: 28px;
  overflow: hidden;
  background: ${props => props.theme.cardBackground};
  box-shadow: 0 30px 80px rgba(124, 58, 237, 0.18), 0 8px 24px rgba(0,0,0,0.08);
  z-index: 2;

  @media (max-width: 768px) {
    aspect-ratio: auto;
    height: calc(100vh - 32px);
    max-height: none;
    border-radius: 20px;
  }
`

const HeroBg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
  z-index: 1;
  pointer-events: none;

  @media (max-width: 768px) {
    opacity: 0.25;
  }
`

const TopActions = styled.div`
  position: absolute;
  top: 24px;
  right: 32px;
  display: flex;
  gap: 10px;
  z-index: 5;

  @media (max-width: 600px) {
    top: 16px;
    right: 16px;
  }
`

const TopButton = styled.button`
  background: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  color: ${props => props.theme.textPrimary};
  padding: 9px 18px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);

  &:hover {
    border-color: #7c3aed;
    color: #7c3aed;
    transform: translateY(-1px);
  }
`

const FloatingCard = styled.div`
  position: absolute;
  top: 50%;
  right: 5%;
  transform: translateY(-50%);
  width: 420px;
  background: ${props => props.theme.cardBackground};
  border-radius: 20px;
  padding: 36px 32px 28px;
  box-shadow: 0 20px 60px rgba(124, 58, 237, 0.30), 0 6px 20px rgba(0,0,0,0.10);
  z-index: 4;

  @media (max-width: 1100px) {
    width: 380px;
    padding: 32px 28px 24px;
  }
  @media (max-width: 900px) {
    top: 50%;
    left: 50%;
    right: auto;
    transform: translate(-50%, -50%);
    width: min(420px, calc(100% - 32px));
  }
  @media (max-height: 720px) {
    padding: 24px 28px 20px;
  }
  @media (max-width: 480px) {
    padding: 28px 22px 22px;
    border-radius: 18px;
  }
`

const Brand = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 18px;
  img { width: 88px; height: auto; }
`

const CardTitle = styled.h1`
  text-align: center;
  font-size: 24px;
  font-weight: 800;
  color: ${props => props.theme.textPrimary};
  margin: 0 0 28px;
  letter-spacing: -0.3px;
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
`

const InputBox = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  svg.leading {
    position: absolute;
    left: 14px;
    color: ${props => props.theme.textMuted};
    pointer-events: none;
  }
`

const StyledInput = styled.input`
  flex: 1;
  width: 100%;
  padding: 13px 14px 13px 44px;
  border: 1.5px solid ${props => props.theme.border};
  border-radius: 10px;
  font-size: 14px;
  background: ${props => props.theme.inputBackground};
  color: ${props => props.theme.textPrimary};
  transition: all 0.2s;

  &::placeholder { color: ${props => props.theme.textMuted}; }
  &:focus {
    outline: none;
    border-color: #7c3aed;
    box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.12);
  }
  &.has-trailing { padding-right: 44px; }
`

const TrailingBtn = styled.button`
  position: absolute;
  right: 10px;
  background: transparent;
  border: none;
  color: ${props => props.theme.textMuted};
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:hover { color: ${props => props.theme.textPrimary}; }
`

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 4px;
`

const RememberLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${props => props.theme.textSecondary};
  cursor: pointer;
  user-select: none;
  input { width: 15px; height: 15px; cursor: pointer; accent-color: #7c3aed; }
`

const ForgotLink = styled.button`
  background: none;
  border: none;
  font-size: 13px;
  color: #7c3aed;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  &:hover { text-decoration: underline; }
`

const SubmitBtn = styled.button`
  margin-top: 8px;
  width: 100%;
  padding: 14px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #7c3aed 0%, #4f46e5 50%, #2563eb 100%);
  color: #ffffff;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 6px 20px rgba(124, 58, 237, 0.35);

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 10px 28px rgba(124, 58, 237, 0.45);
  }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`

const SignupRow = styled.div`
  text-align: center;
  margin-top: 22px;
  font-size: 14px;
  color: ${props => props.theme.textSecondary};

  button {
    background: none;
    border: none;
    color: #7c3aed;
    font-weight: 700;
    font-size: 14px;
    cursor: pointer;
    padding: 0 4px;
    &:hover { text-decoration: underline; }
  }
`

export default function LandingPage() {
  const navigate = useNavigate()
  const { theme } = useTheme()
  const { login } = useAuth()

  const [loginData, setLoginData] = useState({ id: '', password: '' })
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showRegister, setShowRegister] = useState(false)
  const [showRoomModal, setShowRoomModal] = useState(false)
  const [activePage, setActivePage] = useState('home')
  const [showPromoVideo, setShowPromoVideo] = useState(() => {
    const dontShowUntil = localStorage.getItem('promoVideoDontShowUntil')
    if (!dontShowUntil) return true
    return new Date() >= new Date(dontShowUntil)
  })

  const handleLoginChange = (e) => {
    const { name, value } = e.target
    setLoginData(prev => ({ ...prev, [name]: value }))
  }

  const handleLoginSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    try {
      const response = await fetch('/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          username: loginData.id,
          password: loginData.password
        }),
      })
      const contentType = response.headers.get('content-type')

      if (!response.ok) {
        try {
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json()
            alert(`로그인 실패: ${errorData.message || '아이디 또는 비밀번호가 일치하지 않습니다.'}`)
          } else if (response.status === 504) {
            alert('서버 연결 시간이 초과되었습니다.\n잠시 후 다시 시도해주세요.')
          } else {
            alert(`서버 오류가 발생했습니다. (${response.status})`)
          }
        } catch (parseError) {
          console.error('에러 응답 파싱 실패:', parseError)
          alert(`로그인 실패: 서버 응답을 처리할 수 없습니다. (${response.status})`)
        }
        return
      }

      let data
      if (contentType && contentType.includes('application/json')) {
        data = await response.json()
      } else {
        alert('서버에서 올바른 응답을 받지 못했습니다.')
        return
      }

      if (data.success === true && data.user) {
        login(data.user)
        navigate('/home')
      } else {
        alert(`로그인 실패: ${data.message || '아이디 또는 비밀번호가 일치하지 않습니다.'}`)
      }
    } catch (error) {
      console.error('로그인 에러:', error)
      alert('로그인 중 오류가 발생했습니다.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleForgotPassword = () => {
    const email = prompt('비밀번호를 찾을 이메일을 입력하세요:')
    if (email) alert(`${email}로 비밀번호 재설정 링크를 보내드렸습니다.`)
  }

  const handleNavClick = (page) => {
    setActivePage(page)
    if (page === 'home') navigate('/home')
  }

  return (
    <Page theme={theme}>
      <Navbar
        activePage={activePage}
        setActivePage={handleNavClick}
        onMobileMessageClick={() => {}}
        onMobileLoginClick={() => {}}
      />

      <Main theme={theme}>
        <Container theme={theme}>
        <HeroBg src="/login-hero.png" alt="" />

        <FloatingCard theme={theme}>
          <Brand>
            <img src="/arlogo.png" alt="AR 대탈출" />
          </Brand>

          <CardTitle theme={theme}>로그인</CardTitle>

          <Form onSubmit={handleLoginSubmit}>
            <Field>
              <FieldLabel theme={theme} htmlFor="id">아이디</FieldLabel>
              <InputBox theme={theme}>
                <User size={18} className="leading" />
                <StyledInput
                  theme={theme}
                  type="text"
                  id="id"
                  name="id"
                  placeholder="아이디를 입력하세요"
                  value={loginData.id}
                  onChange={handleLoginChange}
                  autoComplete="username"
                />
              </InputBox>
            </Field>

            <Field>
              <FieldLabel theme={theme} htmlFor="password">비밀번호</FieldLabel>
              <InputBox theme={theme}>
                <Lock size={18} className="leading" />
                <StyledInput
                  theme={theme}
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  className="has-trailing"
                  placeholder="비밀번호를 입력하세요"
                  value={loginData.password}
                  onChange={handleLoginChange}
                  autoComplete="current-password"
                />
                <TrailingBtn
                  theme={theme}
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </TrailingBtn>
              </InputBox>
            </Field>

            <Row>
              <RememberLabel theme={theme}>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                로그인 상태 유지
              </RememberLabel>
              <ForgotLink type="button" onClick={handleForgotPassword}>
                비밀번호를 잊으셨나요?
              </ForgotLink>
            </Row>

            <SubmitBtn type="submit" disabled={isLoading}>
              {isLoading ? '로그인 중...' : '로그인'}
            </SubmitBtn>
          </Form>

          <SignupRow theme={theme}>
            계정이 없으신가요?
            <button type="button" onClick={() => setShowRegister(true)}>회원 가입</button>
          </SignupRow>
        </FloatingCard>
        </Container>
      </Main>

      <RoomEntryModal isOpen={showRoomModal} onClose={() => setShowRoomModal(false)} />
      <RegisterModal isOpen={showRegister} onClose={() => setShowRegister(false)} />

      {showPromoVideo && (
        <PromoVideoPopup onClose={() => setShowPromoVideo(false)} />
      )}
    </Page>
  )
}
